import { formatHours } from '@algo/shared';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { api, qk } from '../../lib/api';
import { signOut } from '../../lib/session';
import { formatClock, useElapsedMs, useTimerState } from '../../lib/studyTimer';
import { useTheme } from '../../lib/useTheme';
import { Button } from '../ui';

const NAV = [
  { to: '/', label: 'Хяналтын самбар', short: 'Самбар', icon: '◈', end: true },
  { to: '/roadmap', label: 'Сургалтын зам', short: 'Зам', icon: '⛰' },
  { to: '/problems', label: 'Бодлогууд', short: 'Бодлого', icon: '⌗' },
  { to: '/daily', label: 'Өдрийн бэлтгэл', short: 'Өдөр', icon: '✓' },
  { to: '/knowledge', label: 'Мэдлэгийн сан', short: 'Мэдлэг', icon: '✎' },
  { to: '/statistics', label: 'Статистик', short: 'Статистик', icon: '📈' },
  { to: '/achievements', label: 'Амжилтууд', short: 'Амжилт', icon: '🏆' },
];

function TierBadge() {
  const { data } = useQuery({ queryKey: qk.dashboard, queryFn: () => api.getDashboard() });
  if (!data) return null;

  return (
    <div className="rounded-lg border border-edge bg-raised px-3 py-2.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wide text-muted">{data.tier}</span>
        <span className="font-mono text-lg font-semibold text-ink">{data.rating}</span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-muted">
        <span>🔥 {data.currentStreak} хоног</span>
        <span>{formatHours(data.totalLearningHours * 60)}</span>
      </div>
      {data.nextTier && (
        <p className="mt-1.5 text-[11px] text-muted">
          {data.nextTier.tier} хүртэл {data.nextTier.pointsAway} оноо
        </p>
      )}
    </div>
  );
}

/** Shows the study timer from any page, so a running session is never forgotten. */
function RunningTimerBadge() {
  const state = useTimerState();
  const elapsed = useElapsedMs(state);
  if (state.startedAt === null && elapsed === 0) return null;

  const running = state.startedAt !== null;
  return (
    <NavLink
      to="/daily"
      className={clsx(
        'flex items-center justify-between rounded-lg border px-3 py-2 text-xs transition',
        running
          ? 'border-accent/50 bg-accent/10 text-accent'
          : 'border-edge bg-raised text-muted hover:text-ink',
      )}
    >
      <span>{running ? '● Хичээллэж байна' : '⏸ Түр зогссон'}</span>
      <span className="font-mono tabular-nums">{formatClock(elapsed)}</span>
    </NavLink>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { theme, toggle } = useTheme();

  return (
    <div className="flex min-h-screen">
      {/* Sticky so the rating and nav stay visible on long pages. */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-4 overflow-y-auto border-r border-edge bg-panel p-4 lg:flex">
        <div className="px-2 py-1">
          <p className="text-sm font-semibold tracking-tight">Algorithm Training OS</p>
          <p className="text-xs text-muted">Хувийн · офлайн · нэг хэрэглэгч</p>
        </div>

        <TierBadge />
        <RunningTimerBadge />

        <nav className="flex flex-col gap-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition',
                  isActive
                    ? 'bg-accent/12 font-medium text-accent'
                    : 'text-muted hover:bg-raised hover:text-ink',
                )
              }
            >
              <span aria-hidden className="w-4 text-center text-xs">
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto space-y-0.5">
          <Button variant="ghost" className="w-full justify-start" onClick={toggle}>
            {theme === 'dark' ? '☾ Харанхуй горим' : '☀ Гэрэлтэй горим'}
          </Button>
          <Button variant="ghost" className="w-full justify-start" onClick={signOut}>
            ⎋ Гарах
          </Button>
        </div>
      </aside>

      {/* Compact navigation for narrow windows. */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex justify-around border-t border-edge bg-panel py-1.5 lg:hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center gap-0.5 px-2 py-1 text-[10px]',
                isActive ? 'text-accent' : 'text-muted',
              )
            }
          >
            <span aria-hidden>{item.icon}</span>
            {item.short}
          </NavLink>
        ))}
      </nav>

      <main className="min-w-0 flex-1 overflow-x-hidden px-4 pb-20 pt-6 sm:px-8 lg:pb-10">
        <div className="mx-auto max-w-[1400px]">{children}</div>
      </main>
    </div>
  );
}
